// shared/currencies.js — the single authority on supported currencies,
// imported by BOTH runtimes. Pure: no imports, no DOM, no node builtins.
// src/js/lib/format.js used to carry its own copy of the CURRENCIES list and
// the symbol lookup, with a comment asking that they be kept in lockstep.
//
// Design (16 Jul 2026): amounts live in their document's currency; there are
// NO conversion features and nothing ever sums across currencies. GBP stays
// the default. CIS is GBP-only (HMRC scheme); non-GBP documents carry no UK
// VAT (international B2B services are typically outside its scope).

export const CURRENCIES = [
  "GBP",
  "USD",
  "EUR",
  "AUD",
  "CAD",
  "NZD",
  "CHF",
  "SEK",
  "NOK",
  "DKK"
];

// What each one is called, for a list a person chooses from. Sentence case
// (COPY.md): the country's adjective keeps its capital, the unit doesn't. Here
// rather than read from Intl.DisplayNames, which says "British Pound" in title
// case and could differ between the browser and the phone's JavaScriptCore.
export const CURRENCY_NAMES = {
  GBP: "British pound",
  USD: "US dollar",
  EUR: "Euro",
  AUD: "Australian dollar",
  CAD: "Canadian dollar",
  NZD: "New Zealand dollar",
  CHF: "Swiss franc",
  SEK: "Swedish krona",
  NOK: "Norwegian krone",
  DKK: "Danish krone"
};

// The list a currency picker offers (8 October 2026). Sterling first, because
// nearly everybody this is for bills in nothing else, then the rest by name.
// Each says its name and its code, "British pound · GBP": the code alone was
// a list of three letters, and code beside symbol read "NOK NOK" for the
// currencies whose symbol IS their code.
export function currencyOptions() {
  const rest = CURRENCIES.filter((code) => code !== "GBP")
    .sort((a, b) => CURRENCY_NAMES[a].localeCompare(CURRENCY_NAMES[b], "en-GB"));
  return ["GBP", ...rest].map((code) => ({
    code,
    name: CURRENCY_NAMES[code],
    symbol: currencySymbol(code),
    label: `${CURRENCY_NAMES[code]} · ${code}`
  }));
}

export function isSupportedCurrency(code) {
  return CURRENCIES.includes(String(code || "").toUpperCase());
}

// Normalise a client-supplied currency: absent → GBP (back-compat), unknown →
// null so routes can 400 rather than mislabel money.
export function normalizeCurrency(code) {
  const raw = String(code || "").trim().toUpperCase();
  if (!raw) return "GBP";
  return CURRENCIES.includes(raw) ? raw : null;
}

// en-GB locale keeps UK number formatting (1,234.56) while Intl supplies the
// right symbol per currency (£, $, €, CHF …).
const formatters = new Map();
export function formatMoney(amount, currency = "GBP") {
  const code = isSupportedCurrency(currency) ? String(currency).toUpperCase() : "GBP";
  if (!formatters.has(code)) {
    formatters.set(code, new Intl.NumberFormat("en-GB", { style: "currency", currency: code }));
  }
  const n = Number.parseFloat(String(amount ?? 0));
  return formatters.get(code).format(Number.isFinite(n) ? n : 0);
}

// Stripe's minimum charge per currency, in major units (their documented
// per-currency floors; card networks reject below these).
export const STRIPE_MINIMUMS = {
  GBP: 0.5,
  USD: 0.5,
  EUR: 0.5,
  AUD: 0.5,
  CAD: 0.5,
  NZD: 0.5,
  CHF: 0.5,
  SEK: 3,
  NOK: 3,
  DKK: 2.5
};

// The bare symbol (£, $, €, CHF) for inline labels.
const symbolCache = new Map();
export function currencySymbol(currency = "GBP") {
  const code = isSupportedCurrency(currency) ? String(currency).toUpperCase() : "GBP";
  if (!symbolCache.has(code)) {
    const part = new Intl.NumberFormat("en-GB", { style: "currency", currency: code })
      .formatToParts(0)
      .find((p) => p.type === "currency");
    symbolCache.set(code, part ? part.value : code);
  }
  return symbolCache.get(code);
}

export function stripeMinimumPence(currency) {
  const min = STRIPE_MINIMUMS[String(currency || "GBP").toUpperCase()] ?? 0.5;
  return Math.round(min * 100);
}
